"use client";

import { CityCombobox, ProvinceCombobox, SubDistrictCombobox, VillageCombobox } from "@entities/common";
import { GovernmentEmployerLocationRegistrationDto, governmentEmployerLocationRegistrationSchema } from "@entities/employer";
import { zodResolver } from "@hookform/resolvers/zod";
import { useClientConfig } from "@providers/app-provider/client-app-provider";
import {
  ComboboxOption,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  GeotaggingInput,
  GeotaggingValue,
  Input,
  Textarea,
} from "@ubud-ui/react-ui-kit";
import { PropsWithChildren } from "react";
import { useForm } from "react-hook-form";

type FormProps = {
  formValue?: GovernmentEmployerLocationRegistrationDto;
  onSubmit?: (data: GovernmentEmployerLocationRegistrationDto) => void;
};

export function GovernmentEmployerLocationRegistrationForm({ formValue, onSubmit, children }: FormProps & PropsWithChildren) {
  const config = useClientConfig();

  const form = useForm<GovernmentEmployerLocationRegistrationDto>({
    resolver: zodResolver(governmentEmployerLocationRegistrationSchema),
    defaultValues: {
      province_id: formValue?.province_id || undefined,
      city_id: formValue?.city_id || undefined,
      sub_district_id: formValue?.sub_district_id || undefined,
      village_id: formValue?.village_id || undefined,
      address: formValue?.address || undefined,
      postal_code: formValue?.postal_code || undefined,
      latitude: formValue?.latitude || undefined,
      longitude: formValue?.longitude || undefined,
    },
  });

  const { watch } = form;

  const handleSubmit = (data: GovernmentEmployerLocationRegistrationDto) => {
    if (onSubmit) {
      onSubmit(data);
    }
  };

  const handleResetLocation = (type: "province" | "city" | "sub_district" | "village") => {
    if (["province"].includes(type)) {
      form.resetField("province_id", {
        defaultValue: null as any,
      });
    }

    if (["province", "city"].includes(type)) {
      form.resetField("city_id", {
        defaultValue: null as any,
      });
    }

    if (["province", "city", "sub_district"].includes(type)) {
      form.resetField("sub_district_id", {
        defaultValue: null as any,
      });
    }

    if (["province", "city", "sub_district", "village"].includes(type)) {
      form.resetField("village_id", {
        defaultValue: null as any,
      });
    }
  };

  const geoValue: GeotaggingValue = {
    latitude: watch("latitude"),
    longitude: watch("longitude"),
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
        <div className="space-y-6">
          <FormField
            control={form.control}
            name="province_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Provinsi <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl className="mt-2">
                  <div>
                    <ProvinceCombobox
                      initialValue={field.value as ComboboxOption}
                      onChange={(option) => {
                        handleResetLocation("province");
                        form.resetField("province_id", {
                          defaultValue: option,
                        });
                      }}
                      onClear={() => {
                        handleResetLocation("province");
                      }}
                    />
                  </div>
                </FormControl>
                <FormMessage className="mt-2" />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 items-start">
            <FormField
              control={form.control}
              name="city_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Kabupaten/kota <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl className="mt-2">
                    <div>
                      <CityCombobox
                        initialValue={field.value as ComboboxOption}
                        onChange={(option) => {
                          handleResetLocation("city");
                          form.resetField("city_id", {
                            defaultValue: option,
                          });
                        }}
                        onClear={() => {
                          handleResetLocation("city");
                        }}
                        disabled={!watch("province_id")?.value}
                        provinceIds={watch("province_id")?.value ? [watch("province_id")?.value] : []}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="mt-2" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sub_district_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Kecamatan <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl className="mt-2">
                    <div>
                      <SubDistrictCombobox
                        initialValue={field.value as ComboboxOption}
                        onChange={(option) => {
                          handleResetLocation("sub_district");
                          form.resetField("sub_district_id", {
                            defaultValue: option,
                          });
                        }}
                        onClear={() => {
                          handleResetLocation("sub_district");
                        }}
                        disabled={!watch("city_id")?.value}
                        cityIds={watch("city_id")?.value ? [watch("city_id")?.value] : []}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="mt-2" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="village_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Kelurahan <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl className="mt-2">
                    <div>
                      <VillageCombobox
                        initialValue={field.value as ComboboxOption}
                        onChange={(option) => {
                          handleResetLocation("village");
                          form.resetField("village_id", {
                            defaultValue: option,
                          });
                        }}
                        onClear={() => {
                          handleResetLocation("village");
                        }}
                        disabled={!watch("sub_district_id")?.value}
                        subDistrictIds={watch("sub_district_id")?.value ? [watch("sub_district_id")?.value] : []}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="mt-2" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="postal_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Kode pos <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl className="mt-2">
                    <Input
                      placeholder="Isi kode pos"
                      maxLength={5}
                      minLength={1}
                      value={field.value || ""}
                      onInput={(e) => field.onChange(e)}
                    />
                  </FormControl>
                  <div className="text-gray-500 text-sm">Masukkan 5 digit kode pos.</div>
                  <FormMessage className="mt-2" />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Alamat lengkap entitas <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl className="mt-2">
                  <Textarea
                    placeholder="Masukkan alamat lengkap"
                    className="h-24"
                    value={field.value || ""}
                    onInput={(e) => field.onChange(e)}
                  />
                </FormControl>
                <FormControl className="mt-2">
                  <GeotaggingInput
                    apiKey={config?.config?.googleMapsApiKey || ""}
                    value={geoValue}
                    mapHeight={200}
                    onChange={(val) => {
                      form.setValue("latitude", val?.latitude || (null as any));
                      form.setValue("longitude", val?.longitude || (null as any));
                    }}
                    onAddressChange={(val) => {
                      field.onChange(val || "");
                    }}
                  />
                </FormControl>
                <FormMessage className="mt-2" />
              </FormItem>
            )}
          />
        </div>

        {children}
      </form>
    </Form>
  );
}
